import isPropValid from '@emotion/is-prop-valid'
import { PlayButton, Button, ScrollDown, Container } from '@hg/stylizator/components'
import { useSizes } from '@hg/stylizator/hooks'
import { BackgroundBlob1, BackgroundBlob2 } from '@hg/stylizator/icons'
import { Text } from '@hg/stylizator/typography'
import { getSpace, Spacing, Flex, themeColors, media, desktopLarge } from '@hg/stylizator/utils'
import * as React from 'react'
import styled, { css } from 'styled-components'

import { ThemeTypeData } from 'app/blocks/sections/HomepageHero/models'
import Buttons from 'app/components/CustomBlocks/component/Buttons'
import HomepageHeroInnerBlocks from 'app/components/CustomBlocks/Section/HomepageHeroSectionBlock/HomepageHeroInnerBlocks'
import useHasMounted from 'app/hooks/use-has-mounted'
import { useGlobalState } from 'app/store'

export interface IDownloadPlatform {
  platform: string
  icon: React.ReactElement
  link: string
  enabled: boolean
}

interface IProps {
  className?: string
  theme: 'default' | 'special' | 'christmas' | 'mintgreen'
  headingText: string
  themedHeadingText?: string
  subheadingText: string
  footerText?: string
  illustration: React.ReactNode
  leftButton?: React.ReactNode
  rightButton?: React.ReactNode
  videoUrl?: string
  videoOpened?: boolean
  onVideoCloseClick?: () => void
  onVideoPlayClick?: () => void
  innerBlocks?: React.ReactNode
  isDownloadEnabled?: boolean
  downloads?: IDownloadPlatform[]
  fullHeight?: boolean
}

interface IContainerStyle extends Pick<IProps, 'fullHeight'> {
  backgroundcolor: string
}

const TOP_MENU_HEIGHT = '64px'

const HomepageHeroSection: React.FC<IProps> = (props) => {
  const hasMounted = useHasMounted()
  const { isDesktop } = useSizes()
  const innerBlocksOnTop = !isDesktop
  const theme = props.theme || 'default'
  const [_, setIsHeroCampaignCTAVisible] = useGlobalState('isHeroCampaignCTAVisible')

  const campaignCTAContainer = React.useRef(null)

  const handleCampaignState = React.useCallback(([entry]: any) => {
    if (entry.isIntersecting && ThemeTypeData[theme].isHeroCampaignCTAVisible) {
      setIsHeroCampaignCTAVisible(true)
    } else {
      setIsHeroCampaignCTAVisible(false)
    }
  }, [])

  React.useEffect(() => {
    if (typeof window === 'undefined') return

    const observer = new IntersectionObserver(handleCampaignState, { rootMargin: `-${TOP_MENU_HEIGHT}` })

    if (campaignCTAContainer.current) {
      observer.observe(campaignCTAContainer.current)
    }

    return () => {
      if (campaignCTAContainer.current) {
        observer.unobserve(campaignCTAContainer.current)
      }
    }
  }, [handleCampaignState, campaignCTAContainer])

  return (
    <Background
      backgroundcolor={ThemeTypeData[theme].backgroundColor}
      fullHeight={props.fullHeight}
      className={props.className}
    >
      <ContentView>
        <Container>
          <ColumnsView>
            <HeadingColumnWrapper className="col">
              <HeadingWrapper>
                {hasMounted && innerBlocksOnTop && (
                  <HomepageHeroInnerBlocks>{props.innerBlocks}</HomepageHeroInnerBlocks>
                )}
                <Text
                  color={ThemeTypeData[theme].headingTextColor}
                  type="heading_xlarge"
                  useCompact
                  customSpacing={[24, 24]}
                >
                  <h1>
                    {props.headingText}
                    {props.themedHeadingText && (
                      <span style={{ color: themeColors.yellow[50] }}>{props.themedHeadingText}</span>
                    )}
                  </h1>
                </Text>
                <Text
                  color={ThemeTypeData[theme].headingSmallTextColor}
                  type="heading_small"
                  useCompact
                  customSpacing={[32, 56]}
                >
                  <small>{props.subheadingText}</small>
                </Text>

                <div ref={campaignCTAContainer}>
                  <Buttons leftButton={props.leftButton} rightButton={props.rightButton} />
                </div>

                {props.isDownloadEnabled && (
                  <DownloadsContainer>
                    <Spacing top={[1, 3]} bottom={1}>
                      <Text type="body">
                        <DownloadText>Download Carebuzz on:</DownloadText>
                      </Text>
                    </Spacing>
                    <DownloadButtonsContainer>
                      {props?.downloads?.map((it) => {
                        const button = (
                          <Button
                            type="secondary"
                            key={it.platform}
                            className={!it.enabled ? 'disabled' : ''}
                            inactive={!it.enabled}
                            disabled={!it.enabled}
                          >
                            <Flex flexDir="column" align="center" justify="center">
                              {it.icon}
                              <div style={{ wordWrap: 'normal' }}>
                                <Text type="subtitle" color={themeColors.blue[60]} fontWeight={it.enabled ? 700 : 400}>
                                  <span>{it.enabled ? it.platform : 'coming soon'}</span>
                                </Text>
                              </div>
                            </Flex>
                          </Button>
                        )

                        return it.enabled ? (
                          <a href={it.link} target="_blank" key={it.platform} rel="noreferrer">
                            {button}
                          </a>
                        ) : (
                          button
                        )
                      })}
                    </DownloadButtonsContainer>
                  </DownloadsContainer>
                )}
              </HeadingWrapper>
              <BlobsWrapper $specialTheme={ThemeTypeData[theme].specialTheme}>
                <BackgroundBlob1 color={ThemeTypeData[theme].backgroundBlobColor} />
                <BackgroundBlob2 color={ThemeTypeData[theme].backgroundBlobColor} />
              </BlobsWrapper>
            </HeadingColumnWrapper>
            <IllustrationView className="col">
              {props.illustration}
              {props.videoUrl && (
                <PlayView>
                  <PlayButton onClick={props.onVideoPlayClick} />
                </PlayView>
              )}
            </IllustrationView>
          </ColumnsView>
        </Container>
      </ContentView>
      {hasMounted && !innerBlocksOnTop && <HomepageHeroInnerBlocks>{props.innerBlocks}</HomepageHeroInnerBlocks>}
      {props.footerText && (
        <ScrollDown>
          <Text type="body_small" color={ThemeTypeData[theme].footerTextColor}>
            <span>{props.footerText}</span>
          </Text>
        </ScrollDown>
      )}
      {props.videoUrl && props.videoOpened ? (
        <VideoOverlay opened={props.videoOpened} onClick={props.onVideoCloseClick}>
          <IframeWrapper>
            <div>
              <iframe
                width="966"
                height="543"
                src={props.videoUrl}
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
            <CloseIcon onClick={props.onVideoCloseClick}>x</CloseIcon>
          </IframeWrapper>
        </VideoOverlay>
      ) : undefined}
    </Background>
  )
}

const DownloadText = styled.span`
  text-align: center;

  @media ${desktopLarge} {
    text-align: left;
  }
`

const DownloadButtonsContainer = styled(Flex)`
  gap: 16px;
  flex-wrap: wrap;
  justify-content: center;

  @media ${desktopLarge} {
    justify-content: flex-start;
  }
`

const IllustrationView = styled.div`
  position: relative;

  img {
    width: 100%;

    @media ${media.largeDesktop} {
      width: 624px;
    }

    @media ${media.tablet}, ${media.mobile} {
      margin-top: ${getSpace(4)}px;
    }

    @media ${media.mobile} {
      position: relative;
    }
  }
`

const CloseIcon = styled.a`
  position: absolute;
  z-index: 1000;
  top: -10px;
  right: 0;
  font-size: 36px;
  line-height: 1;
  font-weight: 400;
  color: #fff;
  text-decoration: none;
  cursor: pointer;
  -webkit-transition: all 200ms;
  transition: all 200ms;
`

const IframeWrapper = styled.div`
  margin: 0 auto;
  padding: 24px;
  max-width: 864px;
  width: calc(100% - 48px);
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translateX(-50%) translateY(-50%);

  & > div {
    position: relative;
    padding-bottom: 56.25%;
    height: 0;
  }

  iframe {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
  }
`

const VideoOverlay = styled.div<{ opened?: boolean }>`
  position: fixed;
  z-index: ${(props) => (props.opened ? 1000 : -1)};
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  background: rgba(0, 0, 0, 0.5);
  opacity: ${(props) => (props.opened ? 1 : 0)};
  transition: all ease 500ms;
`

const PlayView = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
`

const ColumnsView = styled.div`
  display: flex;
  justify-content: space-between;

  .col {
    width: 50%;
  }

  @media ${media.tablet}, ${media.mobile} {
    flex-direction: column;

    .col {
      width: 100%;
      position: relative;
    }
  }
`

const ContentView = styled.div`
  display: flex;
  height: 100%;
  align-items: center;
`

const HeadingColumnWrapper = styled.div`
  display: flex;
  flex-direction: column;
  position: relative;

  @media ${media.desktop}, ${media.largeDesktop} {
    margin-right: ${getSpace(8)}px;
  }

  @media ${media.tablet} {
    margin-top: ${getSpace(16)}px;
  }
`

const HeadingWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  height: 100%;
  z-index: 10;

  @media ${media.tablet}, ${media.mobile} {
    text-align: center;
  }
`

const BlobsWrapper = styled.div<{ $specialTheme?: boolean }>`
  display: flex;
  flex-direction: column;
  justify-content: space-evenly;
  height: 100%;
  width: 100%;
  position: absolute;
  z-index: 5;

  ${(props) => props.$specialTheme && `opacity: 0.05;`}
`

const Background = styled.div.withConfig({ shouldForwardProp: isPropValid })<IContainerStyle>`
  display: flex;
  width: 100%;
  min-height: 616px;
  height: 100vh;
  background-color: ${({ backgroundcolor }) => backgroundcolor};
  flex-direction: column;
  justify-content: flex-end;
  overflow: hidden;

  @media ${media.tablet}, ${media.mobile} {
    min-height: initial;
    height: initial;
    padding-bottom: 24px;
  }

  ${({ fullHeight }) =>
    fullHeight &&
    css`
      height: calc(100vh - 32px - 20px);

      @media ${media.tablet}, ${media.mobile} {
        padding-top: 48px;
        height: initial;
      }
    `}
`

const DownloadsContainer = styled.div`
  margin-top: 16px;

  & button {
    width: auto;
    height: auto;
    min-width: 88px;
    min-height: 53px;
    padding: 4px 8px;
    color: ${themeColors.blue[60]};
  }

  & button.disabled {
    background: ${themeColors.blue[20]};
    opacity: 0.2;
  }
`

export default HomepageHeroSection
